/**
 * Phase 256c — Quick Resend Credentials Dialog
 *
 * Per-row dropdown action shared by Students.js + HRMS.js. Operator picks
 * Email + / WhatsApp channels (multi-select), backend auto-generates a
 * fresh temp password, dispatches via the same Phase 95-style branded
 * templates as the first-attempt welcome. No password typing needed.
 */
import { useState, useEffect } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Mail, MessageCircle, Loader2, ShieldCheck, KeyRound } from 'lucide-react';
import api from '@/lib/api';

export default function QuickResendCredentialsDialog({
  open,
  onClose,
  role,        // 'student' | 'employee'
  user,        // { id, name, email, phone }
  onComplete,
}) {
  const [emailOn, setEmailOn] = useState(true);
  const [waOn, setWaOn] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (open) {
      // Default channel state when reopening — email if available, else WA
      setEmailOn(!!user?.email);
      setWaOn(!user?.email && !!user?.phone);
      setSending(false);
    }
  }, [open, user]);

  if (!user) return null;
  const noEmail = !user.email;
  const noPhone = !user.phone;

  const handleSend = async () => {
    const channels = [];
    if (emailOn) channels.push('email');
    if (waOn) channels.push('whatsapp');
    if (channels.length === 0) {
      toast.error('Pick at least one channel');
      return;
    }
    setSending(true);
    try {
      const res = await api.post('/onboarding/quick-resend', {
        role,
        user_id: user.id,
        channels,
      });
      const sent = res.data?.sent_via || [];
      const failures = res.data?.failures || [];
      if (sent.length > 0) {
        toast.success(`Credentials re-sent via ${sent.join(' + ')} to ${user.name}`);
      }
      if (failures.length > 0) {
        toast.error(`Some channels failed: ${failures.map(f => `${f.channel} (${f.reason})`).join(', ')}`);
      }
      onComplete?.(res.data);
      onClose?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Resend failed');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="max-w-md" data-testid="quick-resend-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-emerald-600" /> Resend Credentials
          </DialogTitle>
          <DialogDescription className="text-xs">
            Generates a fresh temp password and sends it via the channels you select. The {role} will be prompted to change it on first login.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Recipient summary */}
          <div className="p-3 rounded-lg bg-muted/50 border text-sm space-y-0.5">
            <p className="font-medium">{user.name}</p>
            <p className="text-xs text-muted-foreground">
              {user.email || <span className="italic">no email</span>} · {user.phone || <span className="italic">no phone</span>}
            </p>
          </div>

          {/* Channel pickers */}
          <div className="space-y-2">
            <label className="text-[10px] uppercase font-black tracking-widest text-muted-foreground">Channels</label>
            <div className="grid grid-cols-2 gap-2">
              <label
                className={`flex items-center gap-2 p-3 rounded-xl border cursor-pointer transition-colors ${
                  emailOn ? 'border-emerald-500 bg-emerald-50/60 dark:bg-emerald-950/30' : 'border-slate-200 dark:border-slate-700'
                } ${noEmail ? 'opacity-50 cursor-not-allowed' : ''}`}
                data-testid="qr-channel-email"
              >
                <Checkbox
                  checked={emailOn}
                  onCheckedChange={(v) => !noEmail && setEmailOn(!!v)}
                  disabled={noEmail}
                />
                <Mail className={`h-4 w-4 ${emailOn ? 'text-emerald-600' : 'text-slate-400'}`} />
                <span className="text-xs font-bold">Email</span>
              </label>
              <label
                className={`flex items-center gap-2 p-3 rounded-xl border cursor-pointer transition-colors ${
                  waOn ? 'border-emerald-500 bg-emerald-50/60 dark:bg-emerald-950/30' : 'border-slate-200 dark:border-slate-700'
                } ${noPhone ? 'opacity-50 cursor-not-allowed' : ''}`}
                data-testid="qr-channel-whatsapp"
              >
                <Checkbox
                  checked={waOn}
                  onCheckedChange={(v) => !noPhone && setWaOn(!!v)}
                  disabled={noPhone}
                />
                <MessageCircle className={`h-4 w-4 ${waOn ? 'text-emerald-600' : 'text-slate-400'}`} />
                <span className="text-xs font-bold">WhatsApp</span>
              </label>
            </div>
            {noEmail && noPhone && (
              <p className="text-xs text-destructive">
                This {role} has neither email nor phone on file. Add at least one to resend credentials.
              </p>
            )}
          </div>

          {/* Info box */}
          <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 text-[11px] text-amber-800 dark:text-amber-300 flex items-start gap-2">
            <KeyRound className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span>
              The previous password will be invalidated. The new temp password is auto-generated and only revealed via the channels you pick.
            </span>
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-1">
            <Button variant="outline" className="flex-1" onClick={onClose} disabled={sending}>Cancel</Button>
            <Button
              className="flex-1 bg-emerald-600 hover:bg-emerald-700"
              onClick={handleSend}
              disabled={sending || (!emailOn && !waOn) || (noEmail && noPhone)}
              data-testid="qr-send-btn"
            >
              {sending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Mail className="h-4 w-4 mr-1" />}
              {sending ? 'Sending…' : 'Reset & Send'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
